import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { environment } from '@ecs-environment';
import { tracking } from '@ecs-helpers';
import { isSerialNumberResult, isSparePartListResult } from '@ecs-services/search/search.helper';
import {
    SearchResult,
    SearchResultType,
    SerialNumberSearchResult,
    SparePartListSearchResult,
} from '@ecs-services/search/search.interface';
import { TranslatePipe } from '@ngx-translate/core';
import {
    HeaderConfigService,
    HeaderLoginService,
    LanguageService,
    TrackClickDirective,
    TrackingService,
} from '@os-components';
import {
    SewButton,
    SewHeadline,
    SewLink,
    SewMenuItem2,
    SewPopoverMenu,
    SewSkeleton,
} from '@sew/ui-components-angular/standalone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { endLoading, startLoading } from '../../../../helpers/loading/loading';
import { Basket, BasketItemType, BasketService } from '../../../../services/basket.service';
import { WishlistRequest, WishlistService } from '../../../../services/wish-list.service';
import { SafetyHintTagListComponent } from '../safety-hint-tag-list/safety-hint-tag-list.component';

export const PRODUCT_PICTURE_FALLBACK = 'assets/pictures/placeholder-no-product-image.svg';

@Component({
    selector: 'ecs-product-header',
    templateUrl: './product-header.component.html',
    styleUrls: ['./product-header.component.scss'],
    imports: [
        SafetyHintTagListComponent,
        TrackClickDirective,
        AsyncPipe,
        TranslatePipe,
        SewHeadline,
        SewButton,
        SewLink,
        SewMenuItem2,
        SewPopoverMenu,
        SewSkeleton,
    ],
})
export class ProductHeaderComponent implements OnInit {
    private readonly wishlistService = inject(WishlistService);

    @Input() public searchResult?: SerialNumberSearchResult | SparePartListSearchResult;
    @Input() public searchterm?: string;

    public productImageHasLoaded = false;
    public productPictureFallback = PRODUCT_PICTURE_FALLBACK;

    public showProductDataUrl!: Observable<string>;
    public showDocumentationUrl!: Observable<string>;
    public requestRepairServiceUrl?: string;
    public mobileActionsMenuOpen = false;
    public isLoggedIn = false;

    public searchResultType = SearchResultType;
    @ViewChild('basketButton', { read: ElementRef })
    private basketButtonRef!: ElementRef<HTMLSewButtonElement>;
    @ViewChild('wishlistButton', { read: ElementRef })
    private wishlistButtonRef!: ElementRef<HTMLSewButtonElement>;

    public constructor(
        private basketService: BasketService,
        private trackingService: TrackingService,
        private configService: HeaderConfigService,
        private languageService: LanguageService,
        private headerLoginService: HeaderLoginService
    ) {}

    public ngOnInit(): void {
        const country = this.configService.getCountryShort();
        this.showProductDataUrl = this.languageService.onLanguageChange.pipe(
            map((language) => this.getShowProductDataUrl(country, language))
        );
        this.showDocumentationUrl = this.languageService.onLanguageChange.pipe(
            map((language) => this.getShowDocumentationUrl(country, language))
        );
        this.requestRepairServiceUrl = this.getRequestRepairServiceUrl();
        this.headerLoginService.loggedIn.subscribe((isLoggedIn) => (this.isLoggedIn = isLoggedIn));
    }

    public isSerialNumberResult = (
        searchResult: SearchResult | undefined
    ): searchResult is SerialNumberSearchResult => !!searchResult && isSerialNumberResult(searchResult);

    public isSparePartListResult = (
        searchResult: SearchResult | undefined
    ): searchResult is SparePartListSearchResult => !!searchResult && isSparePartListResult(searchResult);

    public addToBasket(productId: string) {
        const basketButton = this.basketButtonRef?.nativeElement;
        startLoading(basketButton);
        const products: Basket = {
            searchterm: this.searchterm ?? '',
            items: [
                {
                    type: BasketItemType.SERIAL_NUMBER,
                    productId,
                    quantity: 1,
                },
            ],
        };
        this.basketService.addProductsToBasket(products, () => {
            endLoading(basketButton);
        });
        this.trackingService.trackEvent(tracking.step.product.addToCart);
    }

    public toggleMobileActionsMenu(): void {
        this.mobileActionsMenuOpen = !this.mobileActionsMenuOpen;
    }

    public closeMobileActionsMenu(): void {
        this.mobileActionsMenuOpen = false;
    }

    public addToWishlist(productId: string) {
        const wishlistButton = this.wishlistButtonRef?.nativeElement;
        startLoading(wishlistButton);
        const request: WishlistRequest = {
            searchTerm: this.searchterm ?? '',
            item: {
                type: 'SERIALNUMBER',
                productId,
                quantity: 1,
            },
        };

        this.wishlistService.addItem(request).subscribe({
            next: () => {
                endLoading(wishlistButton);
                this.wishlistService.showHeaderNotification(1);
            },
            error: () => {
                endLoading(wishlistButton);
                this.wishlistService.showHeaderErrorNotification();
            },
        });
    }

    public getShowProductDataUrl(country: string, language: string): string {
        const serialNumber = this.getSerialNumberForLinkUrl();

        return `${environment.baseURLs.api.domain}/os/dud/?tab=productdata&country=${country}&language=${language}&search=${serialNumber}`;
    }

    public getShowDocumentationUrl(country: string, language: string): string {
        const serialNumber = this.getSerialNumberForLinkUrl();

        return `${environment.baseURLs.api.domain}/os/dud/?tab=documents&country=${country}&language=${language}&search=${serialNumber}`;
    }

    public getRequestRepairServiceUrl() {
        let serialNumber = '';
        if (this.isSerialNumberResult(this.searchResult)) {
            serialNumber = this.getSerialNumberForLinkUrl();
        }

        return `${environment.baseURLs.api.domain}/os/c/main.do?viewset=IM&addon=IM&action=PRODUCT_SKIP_PREVIOUS&SEARCH_STRING=${serialNumber}&SUBMIT_PRODUCT=X&layout_process_step=40&process=IM&SERVICE_TYPE=I&DIRECT_LINK=X`;
    }

    private getSerialNumberForLinkUrl(): string {
        return (
            (this.searchResult?.type === SearchResultType.SERIALNUMBER
                ? this.searchResult.serialNumber
                : '') || ''
        );
    }
}
